import React, { useEffect, useState } from "react";
import { Wallet, Users, TrendingUp } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import GrowthDividendCard from "@/components/GrowthDividendCard";
import { RecordCard, CardGrid, FilterStrip, StatusBadge, EmptyTile, SkeletonTiles, Tile } from "@/components/bento";

export default function Earnings() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const { percentages } = useSettings();
  useEffect(() => { api.get("/earnings").then((r) => setData(r.data)); }, []);
  if (!data) return <SkeletonTiles n={3} cols={3} testId="earnings-loading" />;

  const items = data.items || [];
  const direct = items.filter((e) => e.type === "direct").length;
  const team = items.length - direct;
  const shown = items.filter((e) => filter === "all" || (filter === "direct" ? e.type === "direct" : e.type !== "direct"));
  const levels = ["L1", "L2", "L3", "L4", "L5", "L6", "L7", "L8", "L9", "L10"];
  const earnedLevels = levels.map((lv, idx) => ({ lv, idx, val: data.by_level?.[lv] || 0 })).filter((x) => x.val > 0);

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Income</span>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">Earnings</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 stagger">
        <Tile dark span={3} title="Total earned" icon={Wallet} testId="earnings-total">
          <div className="font-display text-5xl font-black relative z-10">{formatINR(data.total)}</div>
          <div className="text-xs text-white/60 mt-2 relative z-10">{items.length} credits · direct + 10-grade team income</div>
        </Tile>
        <Tile span={3} tone="bg-saffron-50 border-saffron-200" title="Direct earnings" icon={TrendingUp} testId="earnings-direct">
          <div className="font-display text-3xl font-black">{formatINR(data.direct_total)}</div>
          <div className="text-xs text-stone-500 mt-1">{direct} payouts from your own recommendations</div>
        </Tile>
        <Tile span={2} title="Team income (10-grade)" icon={Users} testId="earnings-team">
          <div className="font-display text-3xl font-black">{formatINR(data.team_total ?? data.mlm_total)}</div>
          <div className="text-xs text-stone-500 mt-1">{team} payouts from your network</div>
        </Tile>
        <Tile span={4} title="Team income breakdown">
          {earnedLevels.length === 0 ? (
            <div className="text-sm text-stone-500 italic" data-testid="earnings-no-grade-yet">
              You haven&apos;t earned a team commission yet. Once your team closes their first recommendation, the grade that earned for you will appear here.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {earnedLevels.map(({ lv, idx, val }) => (
                <div key={lv} className="bg-stone-50 border border-stone-200 rounded-xl p-3" data-testid={`team-breakdown-${lv}`}>
                  <div className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">{lv} · {percentages?.[idx] ?? "?"}%</div>
                  <div className="font-display text-base font-black mt-1">{formatINR(val)}</div>
                </div>
              ))}
            </div>
          )}
        </Tile>
      </div>

      <GrowthDividendCard />

      <FilterStrip testId="earnings-filter" active={filter} onChange={setFilter}
        chips={[{ k: "all", label: "All credits", count: items.length }, { k: "direct", label: "Direct", count: direct }, { k: "team", label: "Team", count: team }]} />

      <CardGrid cols={3} dense testId="earnings-history-table">
        {shown.length === 0 && <EmptyTile testId="earnings-empty" title="No earnings yet" desc="Your commission credits will appear here as soon as a lead is paid." />}
        {shown.map((e) => (
          <RecordCard key={e.id} dense testId={`earning-${e.id}`}
            title={e.product_name || "Commission"} sub={e.type === "direct" ? "From your own sale" : `From ${e.from_user_name}`}
            badge={e.type === "direct" ? <StatusBadge size="xs" tone="emerald" label="DIRECT" /> : <StatusBadge size="xs" tone="amber" label={`L${e.level} TEAM`} />}
            right={<div className="font-mono font-black text-emerald-900">{formatINR(e.amount)}</div>}
            meta={[<span>{e.from_user_name}</span>, <span>{new Date(e.created_at).toLocaleDateString()}</span>]} />
        ))}
      </CardGrid>
    </div>
  );
}
